import React from 'react';
import { Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ReportType } from '../../pages/Reports/Reports';
import { useTranslation } from 'react-i18next';
import styles from './ReportFilters.module.css';

export interface ReportFiltersState {
  date_from?: string;
  date_to?: string;
  document_type?: string;
  document_status?: string;
  category?: string;
}

const DOCUMENT_TYPES: { value: string; labelKey: string }[] = [
  { value: 'A', labelKey: 'Awizacja' },
  { value: 'PZ', labelKey: 'Przyjęcie na magazyn' },
  { value: 'WZ', labelKey: 'Wydanie z magazynu' },
  { value: 'KOR', labelKey: 'Korekta stanu' },
];

const DOCUMENT_STATUSES: { value: string; labelKey: string }[] = [
  { value: 'oczekujaca', labelKey: 'Oczekująca' },
  { value: 'zweryfikowano', labelKey: 'Zweryfikowano' },
  { value: 'nowy', labelKey: 'Nowy' },
  { value: 'zrealizowany', labelKey: 'Zrealizowany' },
  { value: 'w_realizacji', labelKey: 'W trakcie realizacji' },
  { value: 'wyslana', labelKey: 'Wysłana' },
  { value: 'dostarczona', labelKey: 'Dostarczona' },
  { value: 'anulowany', labelKey: 'Anulowany' },
];

interface ReportFiltersProps {
  reportType: ReportType | null;
  filters: ReportFiltersState;
  onChange: (filters: ReportFiltersState) => void;
}

const ReportFilters: React.FC<ReportFiltersProps> = ({ reportType, filters, onChange }) => {
  const { t } = useTranslation();

  const update = (key: keyof ReportFiltersState, value: string | undefined) => {
    const next = { ...filters };
    if (value === '' || value === undefined) {
      delete next[key];
    } else {
      (next as Record<string, string>)[key] = value;
    }
    onChange(next);
  };

  if (!reportType) return null;

  return (
    <Box className={styles.root}>
      <Typography variant="subtitle1" className={styles.title}>
        {t('reports.filters.title')}
      </Typography>
      <Box className={styles.fields}>
        <TextField
          label={t('reports.filters.dateFrom')}
          type="date"
          value={filters.date_from ?? ''}
          onChange={(e) => update('date_from', e.target.value || undefined)}
          size="small"
          InputLabelProps={{ shrink: true }}
          className={styles.field}
          inputProps={{ max: filters.date_to || undefined }}
        />
        <TextField
          label={t('reports.filters.dateTo')}
          type="date"
          value={filters.date_to ?? ''}
          onChange={(e) => update('date_to', e.target.value || undefined)}
          size="small"
          InputLabelProps={{ shrink: true }}
          className={styles.field}
          inputProps={{ min: filters.date_from || undefined }}
        />
        {reportType === 'documents' && (
          <>
            <FormControl size="small" className={styles.field} sx={{ minWidth: 200 }}>
              <InputLabel id="report-filter-document-type">{t('reports.filters.documentType')}</InputLabel>
              <Select
                labelId="report-filter-document-type"
                value={filters.document_type ?? ''}
                label={t('reports.filters.documentType')}
                onChange={(e) => update('document_type', e.target.value || undefined)}
              >
                <MenuItem value="">{t('reports.filters.all')}</MenuItem>
                {DOCUMENT_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.labelKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" className={styles.field} sx={{ minWidth: 200 }}>
              <InputLabel id="report-filter-document-status">{t('reports.filters.documentStatus')}</InputLabel>
              <Select
                labelId="report-filter-document-status"
                value={filters.document_status ?? ''}
                label={t('reports.filters.documentStatus')}
                onChange={(e) => update('document_status', e.target.value || undefined)}
              >
                <MenuItem value="">{t('reports.filters.all')}</MenuItem>
                {DOCUMENT_STATUSES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.labelKey}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {reportType === 'products' && (
          <TextField
            label={t('reports.filters.category')}
            value={filters.category ?? ''}
            onChange={(e) => update('category', e.target.value || undefined)}
            placeholder={t('reports.filters.categoryPlaceholder')}
            size="small"
            className={styles.field}
            sx={{ minWidth: 200 }}
          />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" className={styles.hint}>
        {t('reports.filters.dateHint')}
      </Typography>
    </Box>
  );
};

export default ReportFilters;
